import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { SeoHead } from "@/components/SeoHead";
import { WebPageJsonLd } from "@/components/marketing/WebPageJsonLd";

const EFFECTIVE_DATE = "May 25, 2026";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title="Privacy Policy — Excerptus"
        description="How Excerptus collects, uses, and protects your data. We do not sell your data and do not train AI models on your content."
      />
      <WebPageJsonLd
        name="Privacy Policy — Excerptus"
        description="How Excerptus collects, uses, and protects your data."
        path="/privacy/"
        dateModified="2026-05-25"
      />
      <MarketingNav />
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <article className="prose-legal">
          <h1 className="font-serif text-4xl font-semibold text-foreground">Privacy Policy</h1>
          <p className="mt-2 text-sm text-muted-foreground">Effective: {EFFECTIVE_DATE}</p>

          <Section title="1. Who we are">
            Excerptus is operated by Knowledge Worker LLC. Contact:{" "}
            <a className="text-primary underline" href="mailto:accounts@excerptus.io">
              accounts@excerptus.io
            </a>.
          </Section>

          <Section title="2. What we collect">
            <ul className="mt-2 list-disc space-y-1 pl-6">
              <li><strong>Account info:</strong> email address, display name, hashed password.</li>
              <li><strong>Your content:</strong> library items and citations, excerpts, notes, tags, categories, Composer drafts, voice profiles, frameworks, brand colors, uploaded logos, and images generated in the Image Studio.</li>
              <li><strong>Connected service data:</strong> if you connect Readwise, we store the highlights and book metadata you choose to import. If you connect a publishing destination (Kit, Bento, WordPress, or others), we store the credentials needed to dispatch on your behalf.</li>
              <li><strong>Usage data:</strong> excerpt counts, image downloads, and connector activity, used to enforce plan limits and surface usage in your account.</li>
              <li><strong>Payment info:</strong> handled by Stripe — we never see or store full card numbers. We store a Stripe customer ID and subscription metadata (plan, status, period end, trial end).</li>
              <li><strong>Bring-your-own keys:</strong> on Pro and above, you can supply your own API keys for image generation (Fal.ai) and on Elite and above, for premium AI inference. We store these keys encrypted and only use them to make calls on your behalf.</li>
              <li><strong>Technical logs:</strong> standard server logs (IP, user agent, timestamps) for security and debugging.</li>
            </ul>
          </Section>

          <Section title="3. How we use it">
            <ul className="mt-2 list-disc space-y-1 pl-6">
              <li>To provide the service — store and display your library and excerpts, generate images, run search, draft content in the Composer, and dispatch publications.</li>
              <li>To process payments and manage your subscription and trial.</li>
              <li>To enforce plan limits and prevent abuse.</li>
              <li>To send essential service emails (password resets, billing receipts, security notices, in-app announcements).</li>
              <li>To improve the product (aggregated, non-identifying usage analysis).</li>
            </ul>
            <p className="mt-3">
              <strong>We do not sell your personal data.</strong> <strong>We do not use your content
              to train AI models</strong> — ours or anyone else's. <strong>We do not run a newsletter
              or marketing-list business</strong> — the only emails we send you are essential service
              messages and in-app announcements you can opt out of.
            </p>
          </Section>

          <Section title="4. Who we share it with">
            We share data only with the service providers we need to run Excerptus, and only the data
            required for each integration:
            <ul className="mt-2 list-disc space-y-1 pl-6">
              <li><strong>Lovable Cloud</strong> — managed database, authentication, file storage, and backend functions that run the app.</li>
              <li><strong>Stripe</strong> — payment processing, subscription management, and billing portal.</li>
              <li><strong>Resend</strong> — delivery of transactional and service emails (sign-up confirmation, password reset, billing notices, in-app announcements you've opted into).</li>
              <li><strong>Excerptus AI</strong> — Composer planning and writing agents, and other in-app AI features. On Elite and Agency, you can route to your own provider via a bring-your-own key.</li>
              <li><strong>Citation lookup</strong> — Crossref, Google Books, and Open Library, queried on your behalf when you add a library item.</li>
              <li><strong>Readwise</strong> (Plus and above, optional) — only when you connect it, to import the highlights and book metadata you choose.</li>
              <li><strong>Publishing destinations</strong> (optional) — Kit, Bento, WordPress, Buttondown, Beehiiv, and Substack, only when you dispatch content. Credentials and content move at your direction.</li>
              <li><strong>Fal.ai</strong> (Pro and above, optional) — called using your own API key for the upcoming Post Image generator (Beta).</li>
              
            </ul>
            We may also disclose data if required by law (subpoena, court order) or to protect our
            rights and users' safety.
          </Section>

          <Section title="5. Where data is stored">
            Your data is stored on servers operated by our infrastructure providers, primarily in the
            United States. By using Excerptus, you consent to your data being processed in the U.S.
          </Section>

          <Section title="6. How long we keep it">
            We keep your account data for as long as your account is active. If you request account
            closure, we delete your library, excerpts, drafts, uploads, and personal info within 30
            days, except where we're required to retain billing records (typically 7 years for tax
            purposes).
          </Section>

          <Section title="7. Your rights">
            You can:
            <ul className="mt-2 list-disc space-y-1 pl-6">
              <li>Access, correct, or export your workspace data as a JSON download from Settings.</li>
              <li>Delete workspace content from Settings, or request full account closure by emailing us.</li>
              <li>Disconnect any third-party integration (Readwise, publishing destinations, BYOK keys) at any time from Settings.</li>
              <li>Cancel your subscription from Settings or via the Stripe Billing Portal; paid plans remain active through the end of the current billing period.</li>
            </ul>
            <strong className="mt-3 block">California residents (CCPA):</strong> you have the right to
            know what personal information we collect, request deletion, and opt out of any "sale" of
            personal information (we don't sell it). Contact us to exercise these rights — we won't
            discriminate against you for doing so.
          </Section>

          <Section title="8. Cookies and tracking">
            We use a small number of essential cookies (session, auth) needed to keep you signed in. We
            do not use third-party advertising or cross-site tracking cookies.
          </Section>

          <Section title="9. Security">
            We use HTTPS everywhere, hashed passwords, row-level security policies on the database to
            keep your data isolated from other users, and encryption at rest for sensitive surfaces
            including BYOK API keys. No system is perfectly secure — if we ever discover a breach
            affecting your data, we'll notify you promptly as required by law.
          </Section>

          <Section title="10. Children">
            Excerptus is not directed to children under 13, and we don't knowingly collect data from
            them. If you believe a child has created an account, contact us and we'll delete it.
          </Section>

          <Section title="11. Changes to this policy">
            We may update this policy as the product evolves. Material changes will be announced by
            email or in-app notice. The "Effective" date at the top reflects the current version.
          </Section>

          <Section title="12. Contact">
            Privacy questions, deletion requests, or concerns? Email{" "}
            <a className="text-primary underline" href="mailto:accounts@excerptus.io">
              accounts@excerptus.io
            </a>.
          </Section>
        </article>
      </main>
      <MarketingFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="font-serif text-xl font-semibold text-foreground">{title}</h2>
      <div className="mt-2 text-base leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}
